import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "sonner";
import { Loader2, Download, ChevronDown, ChevronRight, Filter } from "lucide-react";
import { PageHeader } from "@/pages/Accounts";

const ENTITY_TYPES = ["", "account", "project", "user", "template", "campaign",
  "contact_list", "consent", "dnd", "api_key", "segment"];

const COMMON_ACTIONS = [
  "", "create", "update", "delete", "login",
  "campaign.create", "campaign.send", "campaign.set-skip-consent",
  "campaign.make-recurring", "campaign.stop-recurring", "campaign.poll-dlr-now",
  "account.dlr-config", "account.dlr-test",
  "api-key.create", "api-key.revoke", "api-key.rotate",
];

export default function AuditPage() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState({
    action: "", entity_type: "", entity_id: "", actor_user_id: "", limit: 200,
  });
  const [expanded, setExpanded] = useState({});
  const [users, setUsers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(q).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { data } = await api.get(`/audit?${params.toString()}`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(q).forEach(([k, v]) => { if (v && k !== "limit") params.set(k, v); });
      params.set("limit", "5000");
      const r = await api.get(`/audit/export.csv?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const actorName = (row) => {
    if (row.actor_type === "api_key") return "API key";
    if (!row.actor_user_id) return "system";
    const u = users.find(x => x.id === row.actor_user_id);
    return u?.name || u?.email || row.actor_user_id.slice(-6);
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-start justify-between">
        <PageHeader title="Audit log" description="Every write action is recorded. Filter by actor, action, or entity." />
        <Button variant="outline" size="sm" onClick={exportCsv} data-testid="audit-export-csv">
          <Download className="h-3 w-3 mr-1" />Export CSV
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Filter className="h-4 w-4" />Filters</CardTitle>
          <CardDescription>Combine filters — all are AND-ed.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Action</Label>
            <Select value={q.action || "__all__"} onValueChange={(v) => setQ({ ...q, action: v === "__all__" ? "" : v })}>
              <SelectTrigger data-testid="audit-filter-action"><SelectValue placeholder="Any action" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">— any —</SelectItem>
                {COMMON_ACTIONS.filter(Boolean).map(a => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Entity type</Label>
            <Select value={q.entity_type || "__all__"} onValueChange={(v) => setQ({ ...q, entity_type: v === "__all__" ? "" : v })}>
              <SelectTrigger data-testid="audit-filter-entity"><SelectValue placeholder="Any entity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">— any —</SelectItem>
                {ENTITY_TYPES.filter(Boolean).map(e => (<SelectItem key={e} value={e}>{e}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Actor (user id)</Label>
            <Select value={q.actor_user_id || "__all__"} onValueChange={(v) => setQ({ ...q, actor_user_id: v === "__all__" ? "" : v })}>
              <SelectTrigger data-testid="audit-filter-actor"><SelectValue placeholder="Any actor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">— any —</SelectItem>
                {users.map(u => (<SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Entity ID</Label>
            <Input
              placeholder="e.g. 6a4e8b2f…"
              className="font-mono text-xs"
              value={q.entity_id}
              onChange={(e) => setQ({ ...q, entity_id: e.target.value })}
              data-testid="audit-filter-entity-id"
            />
          </div>
          <div className="md:col-span-4 flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => { setQ({ action: "", entity_type: "", entity_id: "", actor_user_id: "", limit: 200 }); setTimeout(load, 0); }} data-testid="audit-reset">
              Reset
            </Button>
            <Button size="sm" onClick={load} data-testid="audit-apply">Apply</Button>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="audit-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>When (UTC)</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} className="text-center py-8"><Loader2 className="inline h-4 w-4 animate-spin" /></TableCell></TableRow>
              )}
              {!loading && items?.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-xs text-muted-foreground">No matching audit rows.</TableCell></TableRow>
              )}
              {!loading && items?.map((r) => {
                const isOpen = !!expanded[r.id];
                const hasDiff = r.before || r.after;
                return (
                  <AuditRow
                    key={r.id}
                    row={r}
                    isOpen={isOpen}
                    hasDiff={hasDiff}
                    actorName={actorName}
                    onToggle={() => setExpanded({ ...expanded, [r.id]: !isOpen })}
                  />
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}


function AuditRow({ row, isOpen, hasDiff, actorName, onToggle }) {
  const r = row;
  return (
    <>
      <TableRow data-testid={`audit-row-${r.id}`}>
        <TableCell>
          {hasDiff && (
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onToggle} data-testid={`audit-expand-${r.id}`}>
              {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </Button>
          )}
        </TableCell>
        <TableCell className="font-mono text-xs">{r.created_at?.slice(0, 19).replace("T", " ")}</TableCell>
        <TableCell className="text-xs">
          <span className="font-medium">{actorName(r)}</span>
          {r.actor_type === "api_key" && <Badge variant="secondary" className="ml-2 text-[9px]">API</Badge>}
        </TableCell>
        <TableCell>
          <Badge variant="outline" className="text-[10px] font-mono">{r.action}</Badge>
        </TableCell>
        <TableCell className="text-xs">{r.entity_type}</TableCell>
        <TableCell className="font-mono text-xs">{r.entity_id ? r.entity_id.slice(-8) : "—"}</TableCell>
        <TableCell className="font-mono text-xs">{r.ip || "—"}</TableCell>
      </TableRow>
      {isOpen && hasDiff && (
        <TableRow className="bg-muted/40">
          <TableCell colSpan={7} className="text-xs">
            <div className="grid md:grid-cols-2 gap-4 p-3">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Before</div>
                <pre className="text-[11px] bg-background border rounded p-2 whitespace-pre-wrap max-h-64 overflow-auto font-mono">{r.before ? JSON.stringify(r.before, null, 2) : "—"}</pre>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">After</div>
                <pre className="text-[11px] bg-background border rounded p-2 whitespace-pre-wrap max-h-64 overflow-auto font-mono">{r.after ? JSON.stringify(r.after, null, 2) : "—"}</pre>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
